import { useCallback, useEffect, useState, useContext } from "react";
import ProfileNavigation from "../../../components/common/ProfileNavigation";
import SEO from "../../../components/SEO";
import useUser, { isUserDoctor, hasRole, getAuthorized, postAuthorized, deleteAuthorized } from "../../../hooks/User";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorHandlerContext, ErrorHandlerContextProvider } from "../../../contexts/ErrorHandlerContext";
import ErrorHandler from "../../../components/common/ErrorHandler";
import { ROUTES } from "../../../routes";
import dayjs from "dayjs";
import { getPatientDisplayName, getPatientInitials, hasPatientName } from "../../../utils/patientUtils";
import {
    CalendarIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    CheckIcon,
    UserIcon as UserIconLib,
    SearchIcon as SearchIconLib
} from "../../../components/icons";

import "./patients.css";

// Local Icons - using SearchIconLib from components/icons

const PillIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"/>
        <path d="m8.5 8.5 7 7"/>
    </svg>
);

const DownloadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/>
        <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
);

const UserIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
    </svg>
);

const MailIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="20" height="16" x="2" y="4" rx="2"/>
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
);

const PhoneIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
);

const UsersIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
);

const UserPlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <line x1="19" y1="8" x2="19" y2="14"/>
        <line x1="22" y1="11" x2="16" y2="11"/>
    </svg>
);

const UserMinusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <line x1="22" y1="11" x2="16" y2="11"/>
    </svg>
);

const AlertTriangleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
);

const SparklesIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
        <path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>
    </svg>
);

const DEFAULT_PAGE_SIZE = 10;

// Confirm Modal Component
function ConfirmModal({ isOpen, onClose, onConfirm, title, message, patientName, confirmText, isLoading, type = "add" }) {
    if (!isOpen) return null;
    
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
                <div className="confirm-icon">
                    <AlertTriangleIcon />
                </div>
                <h3>{title}</h3>
                <p>{message}</p>
                {patientName && <p className="confirm-patient-name">{patientName}</p>}
                
                <div className="confirm-actions">
                    <button className="btn-secondary" onClick={onClose} disabled={isLoading}>
                        Отмена
                    </button>
                    <button 
                        className={`btn-confirm ${type === 'add' ? 'btn-confirm-add' : 'btn-confirm-remove'}`} 
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? "Загрузка..." : confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Tab Button Component
function TabButton({ active, onClick, icon: Icon, label, count }) {
    return (
        <button 
            className={`tab-btn ${active ? 'active' : ''}`}
            onClick={onClick}
        >
            <Icon />
            <span>{label}</span>
            {count !== undefined && <span className="tab-count">{count}</span>}
        </button>
    );
}

// Pagination Component
function Pagination({ currentPage, totalPages, onPageChange, totalItems, pageSize }) {
    if (totalPages <= 1) return null;

    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    return (
        <div className="pagination">
            <div className="pagination-info">
                {startItem}-{endItem} из {totalItems}
            </div>
            <div className="pagination-controls">
                <button 
                    className="pagination-btn"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    title="Предыдущая страница"
                >
                    <ChevronLeftIcon />
                </button>
                
                <span className="pagination-current">
                    {currentPage} / {totalPages}
                </span>
                
                <button 
                    className="pagination-btn"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    title="Следующая страница"
                >
                    <ChevronRightIcon />
                </button>
            </div>
        </div>
    );
}

// Patient Card Component
function PatientCard({ patient, isMyPatient, onAction, onViewSchedule, onViewMedications, onDownloadReport, onViewChat, isLoading, highlighted }) {
    const displayName = getPatientDisplayName(patient);
    const initials = getPatientInitials(patient);
    const hasName = hasPatientName(patient);
    
    return (
        <div className={`patient-card ${highlighted ? "patient-card--highlighted" : ""}`}>
            <div className="patient-card-header">
                <div className="patient-avatar">
                    {hasName ? initials : <UserIcon />}
                </div>
                <div className="patient-status-badges">
                    
                    {!patient.is_active && <span className="status-badge inactive">Неактивен</span>}
                </div>
            </div>
            
            <div className="patient-info">
                <h3 className={`patient-name ${!hasName ? 'no-name' : ''}`} title={displayName}>{displayName}</h3>
                
                {patient.email && (
                    <div className="patient-email">
                        <MailIcon />
                        <span title={patient.email}>{patient.email}</span>
                    </div>
                )}
                
                {patient.phone_number && (
                    <div className="patient-phone">
                        <PhoneIcon />
                        <span title={patient.phone_number}>{patient.phone_number}</span>
                    </div>
                )}
                
                <div className="patient-meta">
                    <span className="patient-id">ID: {patient.id}</span>
                    <span className="patient-date">
                        С {dayjs(patient.creation_dt).format("DD.MM.YYYY")}
                    </span>
                </div>
            </div>
            
            {isMyPatient ? (
                <div className="patient-actions my-patient-actions">
                    <div className="primary-actions">
                        <button 
                            className="btn-action btn-schedule"
                            onClick={() => onViewSchedule(patient)}
                            title="Назначенные опросы"
                        >
                            <CalendarIcon />
                            <span>Опросы</span>
                        </button>
                        <button 
                            className="btn-action btn-medications"
                            onClick={() => onViewMedications(patient)}
                            title="Назначения лекарств"
                        >
                            <PillIcon />
                            <span>Лекарства</span>
                        </button>
                        {onViewChat && (
                            <button 
                                className="btn-action btn-chat"
                                onClick={() => onViewChat(patient)}
                                title="AI Анализ"
                            >
                                <SparklesIcon />
                                <span>AI Анализ</span>
                            </button>
                        )}
                    </div>
                    <div className="secondary-actions">
                        <button 
                            className="btn-action btn-report"
                            onClick={() => onDownloadReport(patient)}
                            title="Скачать отчёт"
                        >
                            <DownloadIcon />
                            <span>Отчёт</span>
                        </button>
                        <button 
                            className="btn-action btn-remove btn-detach"
                            onClick={() => onAction(patient, 'remove')}
                            disabled={isLoading}
                        >
                            {isLoading ? <span className="loading-dots">...</span> : <><UserMinusIcon /><span>Открепить</span></>}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="patient-actions single-action">
                    <button 
                        className="btn-action btn-add"
                        onClick={() => onAction(patient, 'add')}
                        disabled={isLoading}
                    >
                        {isLoading ? <span className="loading-dots">...</span> : <><UserPlusIcon /><span>Закрепить</span></>}
                    </button>
                </div>
            )}
        </div>
    );
}

// Main Patients Component
function Patients() {
    const { loading: userLoading, error: userError, data: userData } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const highlightPatientId = location.state?.highlightPatientId;
    const { setErrorList } = useContext(ErrorHandlerContext);
    
    const [activeTab, setActiveTab] = useState("my"); // "my" | "available"
    
    // My patients state
    const [myPatients, setMyPatients] = useState([]);
    const [myPatientsLoading, setMyPatientsLoading] = useState(true);
    const [myPatientsTotal, setMyPatientsTotal] = useState(0);
    
    // Available patients state
    const [availablePatients, setAvailablePatients] = useState([]);
    const [availableTotal, setAvailableTotal] = useState(0);
    const [availableLoading, setAvailableLoading] = useState(false);
    
    // Search and pagination
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    
    // Action loading
    const [actionLoadingId, setActionLoadingId] = useState(null);
    
    // Confirm modal state
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        type: null, // 'add' or 'remove'
        patient: null
    });
    
    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);
    
    // Auth check
    useEffect(() => {
        if (userLoading) return;
        if (userError) {
            navigate(ROUTES.AUTH, { viewTransition: true });
            return;
        }
        if (!isUserDoctor(userData)) {
            navigate(ROUTES.PROFILE, { viewTransition: true });
        }
    }, [userLoading, userError, userData, navigate]);
    
    // Load my patients
    const loadMyPatients = useCallback(async () => {
        setMyPatientsLoading(true);
        try {
            const st = (currentPage - 1) * DEFAULT_PAGE_SIZE;
            const params = {
                st,
                limit: DEFAULT_PAGE_SIZE
            };
            
            if (debouncedSearch.trim() && activeTab === "my") {
                params.query = debouncedSearch.trim();
            }
            
            const response = await getAuthorized("/doctor/my_patients", { params });
            
            if (response.data?.patients) {
                setMyPatients(response.data.patients);
                setMyPatientsTotal(response.data.total_count || 0);
            } else {
                setMyPatients([]);
                setMyPatientsTotal(0);
            }
        } catch (error) {
            console.error("Failed to load my patients:", error);
            setErrorList(prev => [...prev, error.response?.data?.error_description || "Ошибка при загрузке пациентов"]);
            setMyPatients([]);
            setMyPatientsTotal(0);
        } finally {
            setMyPatientsLoading(false);
        }
    }, [currentPage, debouncedSearch, activeTab, setErrorList]);
    
    // Load available patients
    const loadAvailablePatients = useCallback(async () => {
        setAvailableLoading(true);
        try {
            const st = (currentPage - 1) * DEFAULT_PAGE_SIZE;
            const params = {
                st,
                limit: DEFAULT_PAGE_SIZE
            };
            if (debouncedSearch.trim()) {
                params.query = debouncedSearch.trim();
            }
            
            const response = await getAuthorized("/doctor/available_patients", { params });
            setAvailablePatients(response.data?.patients || []);
            setAvailableTotal(response.data?.total_count || 0);
        } catch (error) {
            console.error("Failed to load available patients:", error);
            setErrorList(prev => [...prev, "Ошибка при загрузке доступных пациентов"]);
            setAvailablePatients([]);
            setAvailableTotal(0);
        } finally {
            setAvailableLoading(false);
        }
    }, [currentPage, debouncedSearch, setErrorList]);
    
    // Load data based on active tab
    useEffect(() => {
        if (userLoading) return;
        if (activeTab === "my") {
            loadMyPatients();
        } else if (activeTab === "available") {
            loadAvailablePatients();
        }
    }, [activeTab, loadMyPatients, loadAvailablePatients, userLoading, currentPage, debouncedSearch]);
    
    // Handle action click - open confirm modal
    const handleActionClick = useCallback((patient, type) => {
        setConfirmModal({
            isOpen: true,
            type,
            patient
        });
    }, []);
    
    // Handle confirm modal close
    const handleConfirmClose = useCallback(() => {
        setConfirmModal({ isOpen: false, type: null, patient: null });
    }, []);
    
    // Handle associate (add patient)
    const handleAssociate = useCallback(async () => {
        const patient = confirmModal.patient;
        if (!patient) return;
        
        setActionLoadingId(patient.id);
        try {
            await postAuthorized("/doctor/patient_association", { patient_id: patient.id });
            setConfirmModal({ isOpen: false, type: null, patient: null });
            // Refresh available patients and switch to my patients
            await loadAvailablePatients();
            setActiveTab("my");
        } catch (error) {
            console.error("Failed to associate patient:", error);
            setErrorList(prev => [...prev, error.response?.data?.error_description || "Ошибка при закреплении пациента"]);
        } finally {
            setActionLoadingId(null);
        }
    }, [confirmModal.patient, loadAvailablePatients, setErrorList]);
    
    // Handle disassociate (remove patient)
    const handleDisassociate = useCallback(async () => {
        const patient = confirmModal.patient;
        if (!patient) return;
        
        setActionLoadingId(patient.id);
        try {
            await deleteAuthorized("/doctor/patient_disassociation", { patient_id: patient.id });
            setConfirmModal({ isOpen: false, type: null, patient: null });
            loadMyPatients();
        } catch (error) {
            console.error("Failed to disassociate patient:", error);
            setErrorList(prev => [...prev, error.response?.data?.error_description || "Ошибка при откреплении пациента"]);
        } finally {
            setActionLoadingId(null);
        }
    }, [confirmModal.patient, loadMyPatients, setErrorList]);
    
    const handleViewSchedule = useCallback((patient) => {
        navigate(ROUTES.PATIENT_SCHEDULED, {
            viewTransition: true,
            state: { patientId: patient.id, patientName: getPatientDisplayName(patient) }
        });
    }, [navigate]);
    
    const handleViewMedications = useCallback((patient) => {
        navigate(ROUTES.PATIENT_MEDICATIONS, {
            viewTransition: true,
            state: { patientId: patient.id, patientName: getPatientDisplayName(patient) }
        });
    }, [navigate]);

    const handleViewChat = useCallback((patient) => {
        navigate(`${ROUTES.PATIENT_CHAT}?patientId=${patient.id}`, {
            viewTransition: true
        });
    }, [navigate]);

    const handleDownloadReport = useCallback(async (patient) => {
        try {
            const response = await getAuthorized("/doctor/patient_report", {
                params: { patient_id: patient.id },
                responseType: "blob"
            });
            
            const href = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = href;
            link.download = `Отчет по пациенту ${getPatientDisplayName(patient)}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        } catch (error) {
            console.error("Failed to download report:", error);
            setErrorList(prev => [...prev, "Ошибка при скачивании отчёта"]);
        }
    }, [setErrorList]);
    
    const isLoading = activeTab === "my" ? myPatientsLoading : availableLoading;
    const patients = activeTab === "my" ? myPatients : availablePatients;
    const totalCount = activeTab === "my" ? myPatientsTotal : availableTotal;
    const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
    
    // Loading state
    if (userLoading) {
        return (
            <div className="patients-page">
                <ProfileNavigation isDoctor={true} />
                <div className="patients-loading">
                    <div className="spinner"></div>
                    <p>Загрузка...</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className="patients-page">
            <ProfileNavigation isDoctor={isUserDoctor(userData)} />
            
            <div className="patients-container">
                {/* Header */}
                <div className="patients-header">
                    <div className="patients-title">
                        <UsersIcon />
                        <div>
                            <h1>Мои пациенты</h1>
                            <p className="patients-subtitle">Управление привязкой к пациентам</p>
                        </div>
                    </div>
                </div>
                
                {/* Tabs */}
                <div className="patients-tabs">
                    <TabButton 
                        active={activeTab === "available"}
                        onClick={() => setActiveTab("available")}
                        icon={UserPlusIcon}
                        label="Доступные пациенты"
                    />
                    <TabButton 
                        active={activeTab === "my"}
                        onClick={() => setActiveTab("my")}
                        icon={UsersIcon}
                        label="Мои пациенты"
                        count={myPatientsTotal}
                    />
                </div>
                
                {/* Search */}
                <div className="patients-search-section">
                    <div className="search-box">
                        <SearchIconLib />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={activeTab === "my" ? "Поиск по моим пациентам..." : "Поиск доступных пациентов..."}
                            className="search-input"
                        />
                        {searchQuery && (
                            <button 
                                className="search-clear"
                                onClick={() => setSearchQuery("")}
                                title="Очистить поиск"
                            >
                                ×
                            </button>
                        )}
                    </div>
                    {debouncedSearch && (
                        <div className="search-results-info">
                            Найдено: {totalCount}
                        </div>
                    )}
                </div>
                
                {/* Content */}
                {isLoading ? (
                    <div className="patients-loading">
                        <div className="spinner"></div>
                        <p>Загрузка пациентов...</p>
                    </div>
                ) : patients.length === 0 ? (
                    <div className="empty-patients-state">
                        <div className="empty-icon">
                            {activeTab === "my" ? <UsersIcon /> : <UserPlusIcon />}
                        </div>
                        <h3>
                            {activeTab === "my" 
                                ? "У вас нет привязанных пациентов" 
                                : (debouncedSearch ? "Пациенты не найдены" : "Нет доступных пациентов")
                            }
                        </h3>
                        <p>
                            {activeTab === "my"
                                ? "Перейдите во вкладку \"Доступные пациенты\" чтобы привязать пациента"
                                : (debouncedSearch ? "Попробуйте изменить параметры поиска" : "")
                            }
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="patients-stats">
                            <span>Всего: {totalCount}</span>
                        </div>
                        
                        <div className="patients-grid">
                            {patients.map((patient) => (
                                <PatientCard
                                    key={patient.id}
                                    patient={patient}
                                    isMyPatient={activeTab === "my"}
                                    onAction={handleActionClick}
                                    onViewSchedule={handleViewSchedule}
                                    onViewMedications={handleViewMedications}
                                    onDownloadReport={handleDownloadReport}
                                    onViewChat={hasRole(userData, "admin") ? handleViewChat : undefined}
                                    isLoading={actionLoadingId === patient.id}
                                    highlighted={highlightPatientId === patient.id}
                                />
                            ))}
                        </div>
                        
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            totalItems={totalCount}
                            pageSize={DEFAULT_PAGE_SIZE}
                        />
                    </>
                )}
                
                {/* Confirm Modal */}
                <ConfirmModal
                    isOpen={confirmModal.isOpen}
                    onClose={handleConfirmClose}
                    onConfirm={confirmModal.type === 'add' ? handleAssociate : handleDisassociate}
                    title={confirmModal.type === 'add' ? 'Закрепить пациента?' : 'Открепить пациента?'}
                    message={confirmModal.type === 'add' 
                        ? 'Вы уверены, что хотите закрепить этого пациента?' 
                        : 'Вы уверены, что хотите открепить этого пациента?'}
                    patientName={getPatientDisplayName(confirmModal.patient)}
                    confirmText={confirmModal.type === 'add' ? 'Закрепить' : 'Открепить'}
                    isLoading={actionLoadingId === confirmModal.patient?.id}
                    type={confirmModal.type}
                />

            </div>
        </div>
    );
}

// Legacy PatientList component for backwards compatibility (used by Scheduler)
function PatientList({ patients, additional = [] }) {
    if (!patients || patients.length === 0) {
        return <div className="empty-state">Нет пациентов</div>;
    }

    return (
        <table className="legacy-patient-table">
            <thead>
                <tr>
                    <th>Имя</th>
                    <th>Эл. почта</th>
                    {additional.map((_, index) => <th key={index}></th>)}
                </tr>
            </thead>
            <tbody>
                {patients.map((patient, index) => (
                    <tr key={patient.id || index}>
                        <td data-label="Имя:">{getPatientDisplayName(patient)}</td>
                        <td data-label="Email:">{patient.email || "—"}</td>
                        {additional.map((Component, idx) => (
                            <td key={idx} data-label="">
                                <Component patient={patient} />
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

// Export wrapped with ErrorHandler
export default function PatientsPage() {
    return (
        <>
            <SEO 
                title="Мои пациенты" 
                description="Управление списком пациентов и их данными" 
                noindex={true} 
            />
            <ErrorHandlerContextProvider>
                <Patients />
                <ErrorHandler />
            </ErrorHandlerContextProvider>
        </>
    );
}

export { PatientList };
